import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { createReview, getReviews } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";
import { OrnamentDivider, Fleuron } from "@/components/Ornament";
import { ScrollReveal } from "@/components/ScrollReveal";
import { StarRating } from "@/components/StarRating";

const fieldClass =
  "w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-primary transition-colors text-base placeholder:text-foreground/40";

const DATE_LOCALES = { lv: "lv-LV", en: "en-GB", ru: "ru-RU" };

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleDateString(DATE_LOCALES[lang] || "lv-LV", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const ReviewCard = ({ review, lang, t }) => (
  <article
    data-testid={`review-card-${review.id}`}
    className="bg-white/70 border border-gold/40 paper-grain p-6 md:p-8 flex flex-col gap-4"
  >
    <StarRating value={review.rating} readOnly size="sm" testid={`review-rating-${review.id}`} />
    <p className="text-foreground/85 leading-relaxed font-light italic">
      “{review.comment}”
    </p>
    <div className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between gap-3">
      <span className="font-serif text-sm text-foreground/70">
        {review.name?.trim() || t("reviews.anon")}
      </span>
      <span className="text-xs text-foreground/50">
        {formatDate(review.created_at, lang)}
      </span>
    </div>
  </article>
);

const ReviewForm = ({ onCreated }) => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error(t("reviews.form.error_rating"));
      return;
    }
    if (!comment.trim() || comment.trim().length < 2) {
      toast.error(t("reviews.form.error_comment"));
      return;
    }
    try {
      setSubmitting(true);
      const created = await createReview({
        rating,
        comment: comment.trim(),
        name: name.trim() || null,
      });
      toast.success(t("reviews.form.success"));
      setRating(0);
      setName("");
      setComment("");
      onCreated?.(created);
    } catch (err) {
      toast.error(t("reviews.form.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="review-form"
      onSubmit={handleSubmit}
      className="bg-white/70 border border-gold/40 paper-grain p-8 md:p-10 space-y-7"
    >
      <h2 className="font-serif text-3xl md:text-4xl font-light italic">
        {t("reviews.form.title")}
      </h2>

      <div>
        <label className="overline block mb-3">{t("reviews.form.rating_label")}</label>
        <StarRating
          value={rating}
          onChange={setRating}
          size="lg"
          testid="review-form-rating"
        />
        <p className="mt-2 text-xs text-foreground/50">
          {t("reviews.form.rating_help")}
        </p>
      </div>

      <div>
        <label className="overline block mb-2">{t("reviews.form.name_label")}</label>
        <Input
          data-testid="review-form-name"
          className={fieldClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("reviews.form.name_placeholder")}
          maxLength={80}
        />
      </div>

      <div>
        <label className="overline block mb-2">{t("reviews.form.comment_label")}</label>
        <Textarea
          data-testid="review-form-comment"
          className={`${fieldClass} min-h-[140px] resize-none`}
          rows={5}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("reviews.form.comment_placeholder")}
          maxLength={1000}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          data-testid="review-form-submit"
          disabled={submitting}
          className="group inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em] disabled:opacity-50"
        >
          {submitting ? t("reviews.form.submitting") : t("reviews.form.submit")}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </button>
      </div>
    </form>
  );
};

const Reviews = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await getReviews(120);
      setItems(data?.items || []);
      setStats(data?.stats || { average: 0, count: 0 });
    } catch (err) {
      setItems([]);
      setStats({ average: 0, count: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleCreated = () => {
    // Refetch to keep stats exact (avoids client-side rounding drift)
    loadReviews();
  };

  const hasReviews = items.length > 0;
  const headerStars = useMemo(() => Math.round(stats.average || 0), [stats.average]);

  return (
    <div data-testid="reviews-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="reviews-header-banner"
        eyebrow={t("reviews.eyebrow")}
        title={t("reviews.title")}
        lead={t("reviews.lead")}
      />

      <section className="container-roze relative pt-16 md:pt-20">
        <OrnamentDivider className="mb-12" />

        {/* Stats header */}
        {stats.count > 0 && (
          <ScrollReveal className="flex flex-col items-center gap-4 mb-14">
            <div className="flex items-center gap-4">
              <StarRating value={headerStars} readOnly size="lg" testid="reviews-avg-stars" />
              <span
                data-testid="reviews-avg-number"
                className="font-serif text-4xl md:text-5xl font-light"
              >
                {stats.average.toFixed(1)}
              </span>
            </div>
            <p className="text-sm text-foreground/60" data-testid="reviews-count">
              {t("reviews.based_on")} {stats.count} {t("reviews.based_on_suffix")}
            </p>
          </ScrollReveal>
        )}

        {/* Grid of reviews */}
        {!loading && !hasReviews && (
          <ScrollReveal>
            <div
              data-testid="reviews-empty"
              className="text-center py-16 border-y border-gold/40 bg-white/40"
            >
              <Fleuron className="h-5 w-5 text-gold mx-auto mb-4" />
              <p className="font-serif text-2xl md:text-3xl font-light italic text-foreground/70">
                {t("reviews.empty")}
              </p>
            </div>
          </ScrollReveal>
        )}

        {hasReviews && (
          <div
            data-testid="reviews-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-20"
          >
            {items.map((review, idx) => (
              <ScrollReveal key={review.id} delay={idx % 3}>
                <ReviewCard review={review} lang={lang} t={t} />
              </ScrollReveal>
            ))}
          </div>
        )}

        <OrnamentDivider className="my-16" />

        {/* Submission form */}
        <ScrollReveal className="max-w-3xl mx-auto">
          <ReviewForm onCreated={handleCreated} />
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Reviews;
